<template>
  <div class="subscription-form">
    <div v-if="state.step == 'choose'" style="min-height: 2.25rem">
      <slot></slot>
    </div>
    <div v-else-if="state.step == 'confirmation' || state.step == 'offer-requested'" style="min-height: 2.25rem"></div>
    <div v-else class="subscription-form__breadcrumps">
      <a @click="moveToStep('choose')" class="subscription-form__steplink">
        {{ $t("labels.buySubscriptionX", { plan: plan?.label }) }}
      </a>
      <template v-if="isStepVisible('seats')">
        <div>
          <lucide-icon
            name="chevron-right"
            class="color-grey-dark"
            style="margin-top: 1px"
          />
        </div>
        <a @click="moveToStep('seats')" class="subscription-form__steplink">
          {{ $t("words.user", 30) }}
        </a>
      </template>
      <template v-if="isStepVisible('upgrades')">
        <div>
          <lucide-icon
            name="chevron-right"
            class="color-grey-dark"
            style="margin-top: 1px"
          />
        </div>
        <a @click="moveToStep('upgrades')" class="subscription-form__steplink">
          {{ $t("words.upgrade", 30) }}
        </a>
      </template>
      <template v-if="isStepVisible('paymentMethod')">
        <div>
          <lucide-icon
            name="chevron-right"
            class="color-grey-dark"
            style="margin-top: 1px"
          />
        </div>
        <a
          @click="moveToStep('paymentMethod')"
          class="subscription-form__steplink"
        >
          {{ $t("labels.paymentMethod") }}
        </a>
      </template>
      <template v-if="isStepVisible('subscription-time')">
        <div>
          <lucide-icon
            name="chevron-right"
            class="color-grey-dark"
            style="margin-top: 1px"
          />
        </div>
        <a
          @click="moveToStep('subscription-time')"
          class="subscription-form__steplink"
        >
          {{ $t("headings.subscriptionTimeShort") }}
        </a>
      </template>
      <template v-if="isStepVisible('billingAddress')">
        <div>
          <lucide-icon
            name="chevron-right"
            class="color-grey-dark"
            style="margin-top: 1px"
          />
        </div>
        <a
          @click="moveToStep('billingAddress')"
          class="subscription-form__steplink"
        >
          {{ $t("labels.billingAddress") }}
        </a>
      </template>
      <template v-if="isStepVisible('summary')">
        <div>
          <lucide-icon
            name="chevron-right"
            class="color-grey-dark"
            style="margin-top: 1px"
          />
        </div>
        <a @click="moveToStep('summary')" class="subscription-form__steplink">
          {{ $t("headings.paymentOverview") }}
        </a>
      </template>
    </div>

    <div class="subscription-form__body">
      <transition-group name="subscription-form__transition">
        <!-- Page 1: CHOOSE -->
        <section v-if="state.step == 'choose'">
          <div class="subscription-form__header">
            <h1>
              {{ $t("headings.chooseSubscriptionPlan") }}
            </h1>
            <p>{{ $t("messages.chooseSubscriptionPlan") }}</p>
          </div>
          <choose-license-component
            key="step-choose"
            :plans="orderablePlans"
            :users="order.seats"
            @select="selectPlan($event)"
            @update:users="setSeats($event)"
          ></choose-license-component>
        </section>

        <div v-else-if="!isDone" class="subscription-form__main">
          <total-costs
            v-if="plan"
            class="mb-4 subscription-form__costs"
            style="position: sticky; top: 1rem"
            :plan="plan"
            :seats="order.seats"
            :upgrades="order.upgrades"
            :include-vat="countryIsGermany || !state.isForBusiness"
            :vat="state.VAT"
            :years="order.years"
            :per-year="order.renewAutomatically"
          />
          <section
            v-if="plan && state.step == 'seats'"
            key="step-seats"
            class="subscription-form__sheet subscription-form__form"
          >
            <div class="subscription-form__step-header">
              <h1 class="mt-0">{{ $t("headings.usersAmount") }}</h1>
              <p>
                {{ $t("messages.usersAmount") }}
              </p>
            </div>
            <div>
              <div class="flex gap-4 items-center">
                <input-number-big
                  :value="order.seats"
                  :min="1"
                  :step="1"
                  style="max-width: 10em"
                  @update:value="setSeats($event)"
                />
                <span>{{ $t("words.license", order.seats) }}</span>
              </div>

              <p class="message message-info">
                {{ $t("message.pricePerUser", { price: plan.price }) }}
              </p>
              <p>
                {{ $t("texts.licensesExplanation") }}
              </p>
            </div>

            <div class="subscription-form__navarea">
              <div class="subscription-form__navsection">
                <button
                  type="button"
                  class="button button_secondary"
                  @click="stepBack()"
                >
                  <lucide-icon name="chevron-left" />
                </button>
                <div class="subscription-form__navlabel">
                  {{ $t("headings.chooseSubscriptionPlan") }}
                </div>
              </div>
              <div class="ml-auto subscription-form__navsection">
                <button type="button" class="button" @click="stepForward()">
                  {{ $t("actions.continue") }}
                  <lucide-icon name="chevron-right" />
                </button>
                <div class="subscription-form__navlabel">
                  {{ $t("headings.addUpgrades") }}
                </div>
              </div>
            </div>
          </section>

          <section
            v-if="plan && state.step == 'upgrades'"
            key="step-upgrades"
            class="subscription-form__sheet subscription-form__form"
          >
            <div class="subscription-form__step-header">
              <h1 class="mt-0">{{ $t("headings.addUpgrades") }}</h1>
              <p>
                {{ $t("messages.chooseUpgrades") }}
              </p>
            </div>
            <div class="r-50-50 gap-6">
              <subscription-upgrade
                v-for="upgrade in subscriptionUpgrades"
                :key="upgrade.id"
                :upgrade="upgradePlans[upgrade.id]"
                :selected="order.upgrades.includes(upgrade.id)"
                mode="order"
                @select="
                  setUpgrade(upgrade.id, !order.upgrades.includes(upgrade.id))
                "
              />
            </div>

            <div class="subscription-form__navarea">
              <div class="subscription-form__navsection">
                <button
                  type="button"
                  class="button button_secondary"
                  @click="stepBack()"
                >
                  <lucide-icon name="chevron-left" />
                </button>
                <div class="subscription-form__navlabel">
                  {{ $t("headings.usersAmount") }}
                </div>
              </div>
              <div class="subscription-form__navsection">
                <button type="button" class="button" @click="stepForward()">
                  {{ $t("actions.continue") }}
                  <lucide-icon name="chevron-right" />
                </button>
                <div class="subscription-form__navlabel">
                  {{ $t("headings.paymentMethod") }}
                </div>
              </div>
            </div>
          </section>

          <form
            v-if="plan && state.step == 'paymentMethod'"
            @submit.prevent="confirmPaymentMethod"
            class="subscription-form__sheet subscription-form__form column gap-4"
          >
            <div class="subscription-form__step-header">
              <h1 class="mt-0">{{ $t("headings.paymentMethod") }}</h1>
              <p>
                {{ $t("messages.paymentMethod") }}
              </p>
            </div>

            <div>
              <div class="r-50-50 gap-6">
                <input-radio-big
                  v-model:value="paymentMethod"
                  name="payment-type"
                  icon="credit-card"
                  :required="true"
                  :data="paymentMethodsList.stripe"
                  :disabled="paymentProcessing"
                >
                  <template #default>
                    {{ $t("payments.stripe.label") }}
                  </template>
                  <template #details>
                    {{ $t("payments.stripe.details") }}
                  </template>
                </input-radio-big>

                <input-radio-big
                  v-model:value="paymentMethod"
                  name="payment-type"
                  icon="mail"
                  :required="true"
                  :data="paymentMethodsList.invoice"
                  :disabled="paymentProcessing"
                >
                  <template #default>
                    {{ $t("payments.invoice.label") }}
                  </template>
                  <template #details>
                    {{ $t("payments.invoice.details") }}
                  </template>
                </input-radio-big>
              </div>

              <input-checkbox
                v-if="paymentMethod == 'stripe'"
                v-model:value="okayWithStripeCookies"
                :required="true"
                :disabled="paymentProcessing"
                class="mt-6 gap-4"
                style="font-size: 1.5rem"
              >
                <template #label>
                  <i18n-t
                    keypath="labels.okayWithStripeCookies"
                    tag="div"
                    style="font-size: 1rem; line-height: 1.2"
                    scope="global"
                  >
                    <template #stripe>
                      <a
                        href="https://stripe.com/privacy"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Stripe
                      </a>
                    </template>
                  </i18n-t>
                </template>
              </input-checkbox>
            </div>

            <div class="subscription-form__navarea">
              <div class="subscription-form__navsection">
                <button
                  type="button"
                  class="button button_secondary"
                  :disabled="paymentProcessing"
                  @click="stepBack()"
                >
                  <lucide-icon name="chevron-left" />
                </button>
                <div class="subscription-form__navlabel">
                  <template v-if="plan?.upgradeable">
                    {{ $t("headings.addUpgrades") }}
                  </template>
                  <template v-else>
                    {{ $t("headings.chooseSubscriptionPlan") }}
                  </template>
                </div>
              </div>
              <div class="subscription-form__navsection">
                <button
                  v-if="paymentMethod == 'stripe'"
                  type="submit"
                  class="button ml-auto"
                  :disabled="paymentProcessing"
                >
                  <template v-if="paymentProcessing">
                    {{ $t("messages.awaitingPayment") }}
                    <loading-spinner class="ml-2" />
                  </template>
                  <template v-else>
                    {{ $t("actions.toStripe") }}
                    <lucide-icon
                      name="square-arrow-out-up-right"
                      class="ml-2"
                    />
                  </template>
                </button>
                <button v-else type="submit" class="button ml-auto">
                  {{ $t("actions.continue") }}
                  <lucide-icon name="chevron-right" />
                </button>
                <div class="subscription-form__navlabel">
                  {{ $t("headings.inputBillingAddress") }}
                </div>
              </div>
            </div>
          </form>

          <form
            v-if="state.step == 'subscription-time'"
            key="step-subscription-time"
            class="subscription-form__sheet"
          >
            <div class="subscription-form__step-header">
              <h1 class="mt-0">{{ $t("headings.subscriptionTime") }}</h1>
              <p>
                {{ $t("messages.subscriptionTime") }}
              </p>
            </div>
            <div class="column gap-4">
              <div>
                <!-- YEARS -->
                <h3 class="label">
                  {{ $t("headings.term") }}
                </h3>
                <div class="flex gap-4 items-center">
                  <input-number-big
                    v-model:value="order.years"
                    :min="1"
                    :step="1"
                    style="max-width: 10em"
                    @update:value="setYears($event)"
                  />
                  <span>{{ $t("roundUnits.year", order.years) }}</span>
                </div>
              </div>

              <div>
                <h3 class="label">
                  {{ $t("headings.startDate") }}
                </h3>
                <input-radio
                  :value="order.startDate == null"
                  :data="true"
                  :label="$t('labels.orderDate')"
                  @update:value="order.startDate = null"
                />
                <input-radio
                  :value="order.startDate == null"
                  :data="false"
                  :label="$t('labels.customDate')"
                  @update:value="
                    order.startDate = new Date().toISOString().substring(0, 10)
                  "
                />
                <input-text
                  v-if="order.startDate != null"
                  v-model:value="order.startDate"
                  :min="new Date().toISOString().substring(0, 10)"
                  type="date"
                  class="mt-2"
                />
              </div>

              <div>
                <!-- AUTO-RENEW -->
                <h3 class="label">
                  {{ $t("headings.renewAutomatically") }}
                </h3>
                <input-checkbox
                  v-model:value="order.renewAutomatically"
                  class="mt-1"
                  :disabled="order.years > 1"
                  :label="$t('texts.renewAutomatically')"
                />
              </div>
            </div>

            <div class="subscription-form__navarea">
              <div class="subscription-form__navsection">
                <button
                  type="button"
                  class="button button_secondary"
                  @click="stepBack()"
                >
                  <lucide-icon name="chevron-left" />
                </button>
                <div class="subscription-form__navlabel">
                  <template v-if="plan?.upgradeable">
                    {{ $t("headings.addUpgrades") }}
                  </template>
                  <template v-else>
                    {{ $t("headings.chooseSubscriptionPlan") }}
                  </template>
                </div>
              </div>
              <div class="subscription-form__navsection">
                <button type="button" class="button" @click="stepForward()">
                  {{ $t("actions.continue") }}
                  <lucide-icon name="chevron-right" />
                </button>
                <div class="subscription-form__navlabel">
                  {{ $t("headings.inputBillingAddress") }}
                </div>
              </div>
            </div>
          </form>

          <!-- PAYMENT DATA -->
          <form
            v-if="plan && state.step == 'billingAddress'"
            ref="formElement"
            key="step-address"
            class="subscription-form__form subscription-form__sheet"
            @submit.prevent="stepForward()"
          >
            <div class="subscription-form__step-header">
              <h1 class="mt-0">{{ $t("headings.inputBillingAddress") }}</h1>
              <p>
                {{ $t("message.paymentData") }}
              </p>
            </div>

            <div>
              <address-form-fields
                v-model:firstname="address.firstname"
                v-model:lastname="address.lastname"
                v-model:company="address.company"
                v-model:vatId="address.vatId"
                v-model:street="address.street"
                v-model:supplement="address.supplement"
                v-model:zipcode="address.zipcode"
                v-model:city="address.city"
                v-model:country="address.country"
                :include-company="state.isForBusiness"
                :company-required="state.isForBusiness"
                :show-form-errors="state.showFormErrors"
                @update:company="state.touchedCompanyField = true"
              />
            </div>

            <div class="flex">
              <button
                type="button"
                class="button button_secondary"
                @click="stepBack()"
              >
                <lucide-icon name="chevron-left" />
              </button>
              <button class="button ml-auto" type="submit">
                {{ $t("headings.paymentOverview") }}
                <lucide-icon name="chevron-right" />
              </button>
            </div>
          </form>

          <!-- Page 3: SUMMARY -->
          <section
            v-else-if="plan && state.step == 'summary'"
            key="step-summary"
            class="subscription-form__sheet"
          >
            <div class="subscription-form__step-header">
              <h1 class="mt-0">{{ $t("actions.requestOffer") }}</h1>
              <p>{{ $t("texts.checkRequest") }}</p>
            </div>

            <h3 class="mb-2 mt-6">{{ $t("headings.billingAddress") }}</h3>
            <div class="subscription-form__addressrecap">
              <div v-if="address.company && state.isForBusiness">
                {{ address.company }}
              </div>
              <div>{{ address.firstname }} {{ address.lastname }}</div>
              <div>{{ address.street }}</div>
              <div v-if="address.supplement">{{ address.supplement }}</div>
              <div>{{ address.zipcode }} {{ address.city }}</div>
              <div>{{ codeToName(address.country) }}</div>

              <div v-if="address.vatId && state.isForBusiness" class="mt-2">
                {{ $t("labels.vatId") }}{{ $t(":") }} {{ address.vatId }}
              </div>
            </div>

            <hr />

            <h3 class="mb-2">{{ $t("labels.paymentMethod") }}</h3>
            <div class="mb-6">
              <template v-if="paymentMethod == 'quote'">
                {{ $t("labels.quotePayment") }}
              </template>
              <template v-else-if="paymentMethod == 'invoice'">
                {{ $t("labels.invoicePayment") }}
              </template>
            </div>

            <template v-if="paymentMethod == 'quote'">
              <hr />

              <h3 class="mb-2">
                {{ $t("headings.subscriptionTime") }}
              </h3>
              <div class="mb-6" style="line-height: 1.5">
                {{ $t("labels.licenseStartDate") }}:
                <template v-if="order.startDate == null">
                  {{ $t("labels.orderDate") }}
                </template>
                <template v-else>
                  {{ formatDateString(order.startDate) }}
                </template>
                <br />
                {{ $t("labels.licenseDuration") }}: {{ $t("count.years", order.years) }}
                <template v-if="order.renewAutomatically">
                  <br>
                  {{ $t("texts.renewsAutomatically") }}
                </template>
              </div>
            </template>

            <hr />

            <h3>{{ $t("headings.overview") }}</h3>
            <total-costs
              :borderless="true"
              :plan="plan"
              :seats="order.seats"
              :upgrades="order.upgrades"
              :vat="state.VAT"
              :include-vat="countryIsGermany || !state.isForBusiness"
              :years="order.years"
            />

            <form
              v-if="paymentMethod == 'invoice'"
              class="mt-6 flex"
              @submit.prevent="orderSubscription"
            >
              <button
                type="button"
                class="button button_secondary"
                @click="stepBack()"
              >
                <lucide-icon name="chevron-left" />
              </button>

              <button class="button ml-auto" type="submit" :disabled="orderingInProgress">
                <template v-if="!orderingInProgress">
                  {{ $t("actions.buyFinal") }}
                </template>
                <template v-else>
                  {{ $t("labels.orderingInProgress") }}
                </template>
              </button>
            </form>

            <form
              v-if="paymentMethod == 'quote'"
              class="mt-6 flex"
              @submit.prevent="requestQuote"
            >
              <button
                type="button"
                class="button button_secondary"
                @click="stepBack()"
              >
                <lucide-icon name="chevron-left" />
              </button>

              <button class="button ml-auto" type="submit" :disabled="orderingInProgress">
                <template v-if="!orderingInProgress">
                  {{ $t("actions.requestOffer") }}
                </template>
                <template v-else>
                  {{ $t("labels.orderingInProgress") }}
                </template>
              </button>
            </form>
          </section>
        </div>

        <!-- Page 4: CONFIRMATION -->
        <section
          v-else-if="state.step === 'confirmation'"
          key="step-confirmation"
          class="subscription-form__confirmation"
        >
          <div class="subscription-form__confirmationcontent">
            <!--<logo />-->
            <div class="subscription-form__party">🎉</div>
            <h1>{{ $t("headings.orderComplete") }}</h1>
            <p class="subscription-form__confirmationtext">
              {{ $t("texts.orderConfirmation") }}
            </p>

            <div class="message subscription-form__invoicemessage" v-if="invoiceUrl">
              <div class="subscription-form__invoicemessage__badge"><lucide-icon name="mail" /></div>
              <div>
                <p>
                  {{ $t("texts.payInvoice") }}
                </p>
                <a class="button button_secondary" :href="invoiceUrl" target="_blank">
                  <lucide-icon name="download" class="mr-2" /> {{ $t("actions.downloadInvoice") }}
                </a>
              </div>
            </div>

            <div class="ta-center mt-6">
              <button
                type="button"
                class="button"
                @click="emit('done', state.boughtSubscription)"
              >
                {{ $t("labels.startUsingSimcision") }}
              </button>
            </div>
          </div>
        </section>

        <section
          v-else-if="state.step === 'offer-requested'"
          key="step-quote-requested"
          class="subscription-form__confirmation"
        >
          <div class="subscription-form__confirmationcontent">
            <div class="subscription-form__party">🎉</div>
            <h2 class="mt-4 mb-3">{{ $t("headings.offerSent") }}</h2>
            <p class="subscription-form__confirmationtext">
              {{ $t("texts.offerSent") }}
              <template v-if="order.startDate == null">
                {{ $t("texts.quoteActivationOnConfirm") }}
              </template>
              <template v-else-if="order.startDate != null">
                {{ $t("texts.quoteActivationDate", { date: formatDateString(order.startDate) }) }}
              </template>
            </p>
            <router-link
              :to="{ name: 'settings-subscriptions' }"
              type="button"
              class="button mt-4"
              :target="samePage ? '_self' : '_blank'"
              @click="emit('done', null)"
            >
              {{ $t("actions.checkOrderStatus") }}
            </router-link>
          </div>
        </section>
      </transition-group>
    </div>

    <p v-if="state.error" class="message-error" style="grid-area: footer">
      {{ state.error }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { showError } from "@core/ui/alert/alert";
import { formatDateString } from "@/i18n";

import { usePlans, subscriptionUpgrades } from "@manager/plans/plans.store";
import { useUser } from "@/api/user.store";
import { useCountries } from "@/helpers/countries";
import { useSubscriptions } from "@/api/subscriptions.store";

import LucideIcon from "@core/ui/LucideIcon.vue";
import LoadingSpinner from "@core/ui/LoadingSpinner.vue";
import AddressFormFields from "@manager/common/AddressFormFields.vue";
import InputRadioBig from "@core/input/InputRadioBig.vue";
// import InputNumber from "@core/input/InputNumber.vue";
import InputRadio from "@core/input/RadioButton.vue";
import InputText from "@core/input/InputText.vue";
import InputNumberBig from "@core/input/InputNumberBig.vue";
import ChooseLicenseComponent from "./ChooseLicenseComponent.vue";
import TotalCosts from "./TotalCosts.vue";
import InputCheckbox from "@core/input/InputCheckbox.vue";
import SubscriptionUpgrade from "../subscriptions/SubscriptionUpgrade.vue";

import type { LicensePlan } from "@manager/plans/plans.store";
import type { Address } from "@/api/Address";
import type { SetOptional } from "type-fest";
import type { Subscription } from "@/api/subscriptions.store";

type OrderStep =
  | "choose"
  | "seats"
  | "upgrades"
  | "paymentMethod"
  | "subscription-time"
  | "billingAddress"
  | "summary"
  | "confirmation"
  | "offer-requested";

interface Props {
  fixedPaymentMethod?: "stripe" | "invoice" | "quote" | null;
  planId?: string;
  seats?: number;
  stepBar?: boolean;
  samePage?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  stepBar: true,
  samePage: false
});
const emit = defineEmits<{
  (e: "update:step", step: OrderStep): void;
  (e: "update:planId", planId: string | null): void;
  (e: "update:seats", seats: number): void;
  (e: "done", subscription: Subscription | null): void;
}>();

const { t } = useI18n();
const { codeToName } = useCountries();
const { user, loadUser } = useUser();
const subscriptionStore = useSubscriptions();
const { orderablePlans, upgradePlans } = usePlans();

const formElement = ref<HTMLFormElement>();

const lastPaymentAddress = computed(
  () =>
    Object.values(subscriptionStore.subscriptions).sort((a, b) => {
      return a.id < b.id ? 1 : -1;
    })[0]?.billingAddress
);

const order = reactive({
  planId: props.planId ?? (null as null | string),
  seats: props.seats ?? 1,
  upgrades: [] as string[],

  // Quote request: time options
  startDate: null as null | string,
  years: 1,
  renewAutomatically: true
});
watch(
  () => order.planId,
  () => {
    if (order.planId != props.planId) {
      emit("update:planId", order.planId);
    }
  }
);
watch(
  () => order.seats,
  () => {
    emit("update:seats", order.seats);
  }
);

const state = reactive({
  step: "choose" as OrderStep,
  selectedPaymentPeriod: "year" as "year" | "month",

  organizationName: "",

  showFormErrors: false,
  boughtSubscription: null as null | Subscription,
  invoiceDocument: null as null | Blob,
  error: "",

  isForBusiness: true,

  VAT: 0.19,

  touchedCompanyField: lastPaymentAddress.value?.company != ""
});

const isDone = computed(() => {
  return state.step === "confirmation" || state.step === "offer-requested";
});

// ==================
// PLAN

const plan = computed(() => {
  if (order.planId == null) return null;
  return orderablePlans.value?.[order.planId];
});

async function selectPlan(plan: LicensePlan) {
  if (user.value == null) {
    throw new Error("User not loaded");
  }

  if (!plan.canAddSeats) {
    order.seats = 1;
  }

  if (!plan.canBeOrderedDirectly) {
    // send a mail if the license can not be ordered directly
    window.open("mailto:service@simcision.com", "_blank");
  } else {
    order.planId = plan.id;

    stepForward();
  }
}

// ==================
// SEATS

function setSeats(seats: number) {
  order.seats = seats;
}

// ==================
// UPGRADES

function setUpgrade(upgrade: string, value: boolean) {
  if (value) {
    order.upgrades.push(upgrade);
  } else {
    order.upgrades = order.upgrades.filter((u) => u !== upgrade);
  }
}

// ==================
// PAYMENT METHOD

const paymentMethodsList = {
  stripe: "stripe",
  invoice: "invoice",
  quote: "quote"
} as const;
const paymentMethods = Object.values(paymentMethodsList);
const paymentMethod = ref<(typeof paymentMethods)[number] | null>(props.fixedPaymentMethod || null);
const paymentProcessing = ref(false);

const okayWithStripeCookies = ref(false);

async function confirmPaymentMethod(event: Event) {
  console.log("confirm payment method", event);
  if (paymentMethod.value == null) {
    showError(t("errors.noPaymentMethod"));
    return;
  }
  if (paymentMethod.value === "stripe") {
    const planId = order.planId;

    if (planId == null) {
      showError(t("errors.noPlan"));
      return;
    }

    paymentProcessing.value = true;
    try {
      const { sessionId } = await subscriptionStore.goToStripeCheckout(
        {
          planId: planId as "single" | "organization",
          seats: order.seats,
          upgrades: order.upgrades
        },
        {
          sameWindow: props.samePage
        }
      );

      checkSessionStatus(sessionId);
    } catch (e) {
      paymentProcessing.value = false;
      showError(e as Error);
    }
  } else {
    moveToStep("billingAddress");
  }
}

let sessionCheckInterval: null | number | NodeJS.Timeout = null;
function checkSessionStatus(sessionId: string) {
  sessionCheckInterval = setInterval(async () => {
    const session = await subscriptionStore.getStripeCheckoutStatus(sessionId);
    console.log("session", session);
    if (session == null) {
      clearSessionCheckInterval();
      showError(t("texts.orderCanceled"));
    } else if (session.status === "complete") {
      if (session.paymentStatus === "paid") {
        clearSessionCheckInterval();

        // update users licenses by reloading the user
        await loadUser();

        moveToStep("confirmation");
      } else {
        console.log("payment not yet through");
      }
    }
  }, 5000);
}
function clearSessionCheckInterval() {
  if (sessionCheckInterval) {
    clearInterval(sessionCheckInterval);
    paymentProcessing.value = false;
    sessionCheckInterval = null;
  }
}
onBeforeUnmount(clearSessionCheckInterval);

// ==================
// SUBSCRIPTION TIME

function setYears(years: number) {
  order.years = years;
  if (years == 1) {
    order.renewAutomatically = true;
  }
  if (years > 1) {
    order.renewAutomatically = false;
  }
}

// ==================
// PAYMENT

const address = reactive({
  firstname: lastPaymentAddress.value?.firstname ?? "",
  lastname: lastPaymentAddress.value?.lastname ?? "",
  company: lastPaymentAddress.value?.company ?? "",
  vatId: lastPaymentAddress.value?.vatId ?? "",
  street: lastPaymentAddress.value?.street ?? "",
  supplement: lastPaymentAddress.value?.supplement ?? "",
  city: lastPaymentAddress.value?.city ?? "",
  zipcode: lastPaymentAddress.value?.zipcode ?? "",
  country: lastPaymentAddress.value?.country ?? "DE"
});

const billingAddress = computed(() => {
  const billingAddress: SetOptional<Address, "company" | "vatId"> = {
    ...address
  };

  if (!state.isForBusiness) {
    delete billingAddress.company;
    delete billingAddress.vatId;
  }

  return billingAddress;
});

const countryIsGermany = computed(() => address.country == "DE");

// ==================
// REQUEST QUOTE

const orderingInProgress = ref(false);

async function requestQuote() {
  if (plan.value == null) return;
  if (paymentMethod.value != "quote") {
    showError("No quote for non-quote payment");
    return;
  }

  const planId = order.planId;
  if (planId != "single" && planId != "organization") {
    console.warn("Invalid plan", planId);
    return;
  }

  try {
    orderingInProgress.value = true;
    await subscriptionStore.createSubscriptionRequest({
      plan: planId,
      payment: paymentMethod.value,
      billingAddress: billingAddress.value,
      seats: order.seats,
      upgrades: order.upgrades,
      startDate: order.startDate,
      years: order.years,
      renewAutomatically: order.renewAutomatically
    });

    moveToStep("offer-requested");
  } catch (e) {
    showError(e as Error);
  } finally {
    orderingInProgress.value = false;
  }
}

// ==================
// ORDER (via Invoice)

async function orderSubscription() {
  if (plan.value == null) return;
  if (paymentMethod.value != "invoice") {
    showError("Direct order only possible with invoice payment");
    return;
  }

  const planId = order.planId;
  if (planId != "single" && planId != "organization") {
    console.warn("Invalid plan", planId);
    return;
  }

  try {
    orderingInProgress.value = true;
    const { subscription, invoiceDocument } = await subscriptionStore.createSubscription({
      paymentMethod: "invoice",
      plan: planId,
      usesStandardPrices: true,
      billingAddress: billingAddress.value as Address,
      seats: order.seats,
      upgrades: order.upgrades
    });
    state.boughtSubscription = subscription;
    state.invoiceDocument = invoiceDocument;
    moveToStep("confirmation");
  } catch (e) {
    showError(e as Error);
  } finally {
    orderingInProgress.value = false;
  }
}

const invoiceUrl = computed(() => {
  if (state.invoiceDocument == null) return null;
  return URL.createObjectURL(state.invoiceDocument);
});

// ==================
// STEP MANAGEMENT

const steps = computed((): OrderStep[] => {
  if (order.planId == null) return ["choose"];
  const steps: OrderStep[] = ["choose"];

  if (plan.value?.upgradeable) {
    steps.push("seats", "upgrades");
  }

  if (!props.fixedPaymentMethod) {
    steps.push("paymentMethod");
  }

  if (props.fixedPaymentMethod == "quote") {
    steps.push("subscription-time");
  }

  steps.push("billingAddress", "summary");
  return steps;
});

function isStepVisible(step: OrderStep) {
  if (!steps.value.includes(step)) return false;
  return steps.value.indexOf(step) <= steps.value.indexOf(state.step);
}

async function moveToStep(step: OrderStep) {
  if (paymentProcessing.value) {
    // prevent any navigation while payment is in progress
    return;
  }

  if (step == "choose") {
    order.planId = null;
  }
  if (step == "upgrades") {
    paymentMethod.value = props.fixedPaymentMethod ?? null;
  }
  if (step === "summary") {
    if (!formElement.value?.reportValidity()) return false;

    const requiredFields = [
      "firstname",
      "lastname",
      "street",
      "city",
      "zipcode",
      "country"
    ] as const;
    for (const field of requiredFields) {
      if (address[field].trim() == "") return false;
      state.showFormErrors = true;
    }
  } else if (step === "billingAddress") {
    if (order.planId == null) {
      return false;
    }
  }
  state.error = "";
  state.step = step;
  emit("update:step", step);
}

function stepBack() {
  const currentStepName = state.step;
  const currentStepIndex = steps.value.indexOf(currentStepName);
  if (currentStepIndex > 0) {
    moveToStep(steps.value[currentStepIndex - 1]);
  }
}

function stepForward() {
  const currentStepName = state.step;
  const currentStepIndex = steps.value.indexOf(currentStepName);
  if (currentStepIndex < steps.value.length - 1) {
    moveToStep(steps.value[currentStepIndex + 1]);
  }
}

defineExpose({
  plan,
  moveToStep,
  selectPlan
});
</script>

<style lang="scss">
.subscription-form {
  padding: 1rem;
  max-width: 75rem;
  margin: 0 auto;
  width: 100%;
  min-height: 100%;

  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: min-content 1fr min-content;
  grid-template-areas: "stepmenu" "body" "footer";

  &__stepmenu {
    display: flex;
    gap: 1rem;
    grid-area: stepmenu;
    &button {
      background-color: transparent;
      padding: 0.5rem 0.5rem;
      border: none;
      border-bottom: 3px solid var(--color-border);

      &.is-active {
        border-bottom-color: var(--color-action);
      }
    }
  }

  &__breadcrumps {
    grid-area: stepmenu;
    padding: 0.5rem 0;
    font-size: 1rem;
    display: flex;
    gap: 0.5rem;
    color: var(--color-text-medium);
    height: 2.25rem;
  }
  &__steplink {
    color: var(--color-text-medium);
    font-weight: 400;
    cursor: pointer;
    user-select: none;
  }

  &__body {
    grid-area: body;
    position: relative;
  }

  &__main {
    display: grid;
    align-items: start;
    grid-template-columns: minmax(0, 50rem) minmax(min-content, 24rem);
    grid-template-areas: "form costs";
    gap: 1.5rem;
    padding: 1.5rem 0;

    & > h3:first-child {
      margin-top: 0;
    }

    & > h3 {
      margin-top: 2rem;
      margin-bottom: 0.5rem;
    }

    & > h4 {
      margin-bottom: 0.25rem;
    }
  }

  &__sheet {
    background-color: var(--color-background-deep);
    padding: 2rem;
    border-radius: 0.5rem;
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.05);
    grid-area: form;
  }

  &__costs {
    grid-area: costs;
    background-color: var(--color-background-deep);
    padding-top: 1rem;
    padding-inline: 1.5rem;
    padding-bottom: 1rem;
    border-radius: 0.5rem;
    overflow: hidden;
    box-shadow: 0 3px 10px var(--color-shadow-1);
  }

  &__header {
    text-align: center;
    padding-top: 1rem;
    h1 {
      font-size: 2.5rem;
    }
  }

  &__step-header {
    --p: 2rem;
    --m: calc(var(--p) * -1);
    padding: var(--p);
    margin-top: var(--m);
    margin-left: var(--m);
    margin-right: var(--m);
    background-color: var(--color-background);
    border-top-left-radius: inherit;
    border-top-right-radius: inherit;

    p {
      margin-bottom: 0;
      color: var(--color-text-moderate);
    }
  }

  // INPUT

  &__form {
    display: flex;
    flex-direction: column;
    gap: 3rem;
  }
  &__formline {
    display: flex;
    gap: 1rem;
  }

  &__navarea {
    display: flex;
    justify-content: space-between;
    padding-top: 1rem;
    // border-top: 1px solid var(--color-border);
  }
  &__navsection {
    position: relative;
    display: grid;
    grid-template-rows: 1fr min-content;
    justify-items: start;
  }
  &__navlabel {
    pointer-events: none;
    opacity: 0;
    position: absolute;
    white-space: nowrap;
    bottom: 100%;
    margin-bottom: 1rem;
    color: var(--color-text-medium);
    background-color: var(--color-background);
    padding: 0.5rem 0.75rem;
    filter: drop-shadow(0 1px 0.25rem var(--color-shadow-2));
    transition: opacity 250ms ease-out;
    &::before {
      // triangle on top side
      content: "";
      display: block;
      position: absolute;
      bottom: -1rem;
      right: 2.5rem;
      left: auto;
      transform: translateX(-50%);
      border: 0.5rem solid transparent;
      border-top-color: var(--color-background);
    }
  }
  &__navsection:first-child &__navlabel::before {
    left: 2.5rem;
    right: auto;
  }
  &__navsection:last-child {
    justify-items: end;
  }
  button:hover + .subscription-form__navlabel {
    opacity: 1;
  }

  &__addressrecap {
    margin-bottom: 2rem;

    line-height: 1.6;
  }

  &__confirmation {
    padding: 0;
    display: flex;
    justify-content: center;
    flex-direction: column;
    background-color: var(--color-background-deep);
    border-radius: 0.5rem;
    width: max-content;
    margin: auto;

    &content {
      text-align: center;
      margin: 0 auto;
      padding: 3rem 3rem;
      max-width: 38rem;
    }
    &text {
      max-width: 36rem;
      line-height: 1.5;
      text-wrap: balance;
    }
  }

  &__invoicemessage {
    margin-top: 3rem;
    border: 2px dashed var(--color-border);
    padding: 1.5rem;
    position: relative;
    text-wrap: balance;

    &__badge {
      display: flex;
      justify-content: center;
      align-items: center;
      color: var(--color-text-medium);
      margin-top: -3rem;
      margin-bottom: 0.5rem;
      margin-inline: auto;
      height: 3rem;
      width: 3rem;
      font-size: 1.5rem;
      background-color: var(--color-background-deep);
      border: 1px solid var(--color-border);
      border-radius: 50%;
    }
  }

  &__party {
    font-size: 4rem;
    text-align: center;
  }

  &__transition {
    &-enter-active,
    &-leave-active {
      transition:
        opacity 250ms,
        transform 250ms;
    }
    &-enter-from,
    &-leave-to {
      opacity: 0;
    }
    &-enter-from {
      transform: translateY(1rem);
    }
    &-leave-active {
      position: absolute !important;
      width: 100%;
      top: 0;
      left: 0;
      right: 0;
    }
  }
}
</style>
